// NebulaLandingAuth.jsx
//
// atlas.serviceco.com login splash — sleeper edition.
//
// What the world sees: a near-pixel replica of Chrome's
// `DNS_PROBE_FINISHED_NXDOMAIN` error page. Looks like a dead domain.
//
// What Mo sees: same thing, but the page silently captures keystrokes.
// Type your PIN; on the 5th character we hit validate_pin. Success →
// dissolve and hand off to the real app via onAuthenticated. Wrong PIN
// or lockout → tiny 200ms wobble, then idle. No visible "wrong PIN"
// message, no lockout countdown — that information would tell anyone
// looking that something is gated. Mo will know his PIN; if he locks
// himself out he can wait 15 minutes.
//
// ── API contract (unchanged from prior visual) ─────────────────────────
//   Props:
//     onAuthenticated(session) — REQUIRED. Fires after validatePin() resolves
//       session = { code, name, role, permissions, email, issued_at_ms, jwt, jwt_exp_ms }
//
// ── Auth plumbing (do not break) ───────────────────────────────────────
//   1. Window keydown captures 0–9 + a–z + Backspace, up to 5 chars.
//   2. validatePin(code) on 5-char entry.
//   3. On success: localStorage write + setActor + onAuthenticated after a
//      900ms dissolve.
//   4. On failure: tiny content-shake, clear, idle.
//   5. Server lockout (5 fails in 15m): silent, just like a wrong PIN.
//      Internal timer auto-clears at server window expiry.
//
// We intentionally don't import the heavy 3D marquee + ember shader
// anymore — the new screen is static HTML so the bundle drops some
// weight too.

import { useEffect, useRef, useState } from 'react';
import { setActor } from '../../lib/awsdb';
import { validatePin } from '../../lib/auth';

const DOT_COUNT = 5;
const PIN_ACCEPT = /^[0-9a-z]$/;
const SUCCESS_MS = 900;
const ERROR_MS = 600;
const SHAKE_MS = 220;

// Hardcoded because the page must look the same regardless of subdomain.
// www.atlas.serviceco.com → "atlas.serviceco.com." matches Chrome's actual canonicalisation.
const FAKE_HOST = 'atlas.serviceco.com';

// Chrome's actual broken-page glyph (Material "broken document"). Inlined
// as SVG so there's no network fetch that could give the page away.
const BROKEN_PAGE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72" width="72" height="72" fill="none" aria-hidden="true">
  <path d="M14 4 L14 68 L58 68 L58 22 L40 4 Z" stroke="#9AA0A6" stroke-width="3" stroke-linejoin="round" fill="white"/>
  <path d="M40 4 L40 22 L58 22" stroke="#9AA0A6" stroke-width="3" stroke-linejoin="round" fill="none"/>
  <circle cx="28" cy="42" r="2.2" fill="#9AA0A6"/>
  <circle cx="44" cy="42" r="2.2" fill="#9AA0A6"/>
  <path d="M26 56 Q36 48 46 56" stroke="#9AA0A6" stroke-width="2.4" fill="none" stroke-linecap="round"/>
</svg>`;

export default function NebulaLandingAuth({ onAuthenticated }) {
  const [step, setStep] = useState('idle');         // idle | typing | validating | success | error
  const [code, setCode] = useState('');
  const [lockedUntilMs, setLockedUntilMs] = useState(0);
  const busyRef = useRef(false);

  // Push the document title to a single non-printable space so the tab
  // label doesn't shout "atlas.serviceco.com". Browsers fall back to the URL.
  useEffect(() => {
    const prev = document.title;
    document.title = ' ';
    return () => { document.title = prev; };
  }, []);

  // PIN entry — window keydown listener so the user just types.
  useEffect(() => {
    async function onKey(e) {
      if (busyRef.current) return;
      if (step === 'success' || step === 'validating') return;

      if (PIN_ACCEPT.test(e.key) && code.length < DOT_COUNT) {
        if (lockedUntilMs > Date.now()) return;
        const next = code + e.key;
        setCode(next);
        setStep('typing');

        if (next.length === DOT_COUNT) {
          busyRef.current = true;
          setStep('validating');
          const { data, error } = await validatePin(next);

          if (data && data.locked) {
            const retry = Number(data.retry_after_seconds) || 900;
            setLockedUntilMs(Date.now() + retry * 1000);
            setStep('error');
            setTimeout(() => { setCode(''); setStep('idle'); busyRef.current = false; }, ERROR_MS);
            return;
          }
          if (error || !data) {
            setStep('error');
            setTimeout(() => {
              setCode('');
              setStep('idle');
              busyRef.current = false;
            }, ERROR_MS);
            return;
          }

          const session = {
            code: data.code,
            name: data.name,
            role: data.role,
            permissions: data.permissions || {},
            email: data.email || null,
            issued_at_ms: Number(data.issued_at_ms) || Date.now(),
            jwt: data.jwt || null,
            jwt_exp_ms: Number(data.exp_ms) || null,
          };
          try { localStorage.setItem('fon-objectives-session', JSON.stringify(session)); } catch {}
          await setActor(session);
          setStep('success');
          setTimeout(() => {
            onAuthenticated(session);
            busyRef.current = false;
          }, SUCCESS_MS);
        }
        return;
      }

      if (e.key === 'Backspace') {
        setCode(prev => {
          const n = prev.slice(0, -1);
          if (n.length === 0) setStep('idle');
          return n;
        });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, code, onAuthenticated, lockedUntilMs]);

  // 1s tick to auto-clear the lockout window. We never display it; the
  // ticker just exists so a re-attempt after the server window doesn't
  // require a refresh.
  useEffect(() => {
    if (lockedUntilMs <= Date.now()) return undefined;
    const iv = setInterval(() => {
      if (lockedUntilMs <= Date.now()) setLockedUntilMs(0);
    }, 1000);
    return () => clearInterval(iv);
  }, [lockedUntilMs]);

  // Visual states are minimal: the only animation is a 220ms wobble when
  // a PIN is rejected (so Mo gets a tiny acknowledgement that a key was
  // received without showing anything readable to a passer-by).
  // Success fades the whole page so handoff feels intentional.
  const wrapperOpacity = step === 'success' ? 0 : 1;

  return (
    <>
      {/* Inline CSS — keeps everything in one file so a future "delete this
          file" cleanup is a single delete, not a hunt. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        html, body, #root { background: #ffffff !important; margin: 0; padding: 0; }
        @keyframes nla-wobble {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-3px); }
          75% { transform: translateX(3px); }
        }
        .nla-root {
          position: fixed; inset: 0;
          background: #ffffff;
          font-family: Roboto, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
          color: #202124;
          overflow: hidden;
          transition: opacity 600ms ease;
        }
        .nla-content {
          position: absolute;
          top: 18%;
          left: 14%;
          max-width: 540px;
        }
        .nla-content--shake {
          animation: nla-wobble 220ms ease;
        }
        .nla-icon { margin-bottom: 22px; }
        .nla-h1 { font-size: 28px; font-weight: 500; letter-spacing: -0.2px; margin: 0 0 14px 0; color: #202124; }
        .nla-sub { font-size: 14px; line-height: 1.5; color: #5F6368; margin: 0 0 24px 0; }
        .nla-code { font-family: ui-monospace, SFMono-Regular, "JetBrains Mono", monospace; font-size: 11px; letter-spacing: 0.4px; color: #5F6368; text-transform: uppercase; }
        .nla-reload {
          position: absolute;
          right: 14%;
          bottom: 28%;
          background: #1A73E8;
          color: #fff;
          border: 0;
          padding: 9px 26px;
          border-radius: 18px;
          font-family: inherit;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          box-shadow: none;
          letter-spacing: 0.1px;
        }
        .nla-reload:hover { background: #1765c4; }
        .nla-reload:active { background: #155cb0; }
        .nla-reload:focus-visible { outline: 2px solid #1A73E8; outline-offset: 2px; }
      `}</style>

      <div className="nla-root" style={{ opacity: wrapperOpacity }}>
        <div className={`nla-content ${step === 'error' ? 'nla-content--shake' : ''}`}>
          <div className="nla-icon" dangerouslySetInnerHTML={{ __html: BROKEN_PAGE_SVG }} />
          <h1 className="nla-h1">This site can&rsquo;t be reached</h1>
          <p className="nla-sub">
            Check if there is a typo in <span style={{ color: '#202124' }}>{FAKE_HOST}.</span>
          </p>
          <div className="nla-code">DNS_PROBE_FINISHED_NXDOMAIN</div>
        </div>

        {/* The Reload button is a real button — clicking it just reloads
            the page (which Chrome's real Reload does too), keeping up the
            illusion. No-op for the auth state machine. */}
        <button
          type="button"
          className="nla-reload"
          onClick={() => window.location.reload()}
          tabIndex={-1}
        >
          Reload
        </button>
      </div>
    </>
  );
}
